import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Image as ImageIcon, LogOut, ChevronRight, Camera, CalendarDays,
  Plus, Trash2, Edit3, Lightbulb, Sparkles, X,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import EditorTrustBadge from "@/components/EditorTrustBadge";

/**
 * EditorPortal — restricted dashboard for the `editor` role.
 *
 * Editors can:
 *   • Create / open / delete galleries
 *   • Manage photos inside galleries (via AdminGalleryManager)
 *   • Create / edit / delete events
 *   • Read + triage public suggestions, see ARIA's weekly digest
 *
 * Editors CANNOT see: payments, marketplace, chauffeur, analytics,
 * user list, platform settings, or any other admin-only surface.
 */
const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.04, duration: 0.35 } }),
};

const TABS = [
  { id: "galleries", label: "Galleries", icon: ImageIcon },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "suggestions", label: "Suggestions", icon: Lightbulb },
];

const EVENT_TYPES = ["Wedding", "Corporate", "Yacht & Sea", "Nightlife",
  "Concert", "Festival", "Private Party", "Product Launch", "Charity Gala", "General"];

export default function EditorPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("galleries");
  const [galleries, setGalleries] = useState([]);
  const [events, setEvents] = useState([]);
  const [suggestions, setSuggestions] = useState({ items: [], counts: {} });
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);

  // Forms
  const [galleryForm, setGalleryForm] = useState({ open: false, title: "", event_type: "Corporate", event_date: "", description: "", client_email: "" });
  const [eventForm, setEventForm] = useState({ open: false, editing: null, title: "", event_type: "Corporate", event_date: "", location: "", description: "" });

  useEffect(() => {
    const stateToken = location.state?.token;
    if (stateToken) localStorage.setItem("auth_token", stateToken);
    const init = async () => {
      try {
        const meRes = await api.get("/auth/me");
        setMe(meRes.data);
        if (meRes.data?.role === "admin") {
          navigate("/admin/dashboard", { replace: true });
          return;
        }
        if (meRes.data?.role !== "editor") {
          toast.error("Editor access required.");
          navigate("/admin", { replace: true });
          return;
        }
        await Promise.all([loadGalleries(), loadEvents(), loadSuggestions()]);
      } catch {
        toast.error("Please sign in.");
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadGalleries = async () => {
    try { const r = await api.get("/admin/galleries"); setGalleries(r.data || []); } catch { /* silent */ }
  };
  const loadEvents = async () => {
    try { const r = await api.get("/admin/events"); setEvents(r.data || []); } catch { /* silent */ }
  };
  const loadSuggestions = async () => {
    try {
      const r = await api.get("/admin/suggestions");
      setSuggestions({ items: r.data?.items || [], counts: r.data?.counts || {} });
    } catch { /* silent */ }
  };
  const loadDigest = async (force = false) => {
    try {
      // GPT-5 can take 40-90s; bump axios timeout well above the default
      // so the editor sees the digest instead of a transient error.
      const r = await api.get(
        `/admin/suggestions/aria-weekly${force ? "?force=1" : ""}`,
        { timeout: 120000 },
      );
      setDigest(r.data);
    } catch { toast.error("Couldn't load ARIA digest."); }
  };

  const handleLogout = async () => {
    try { await api.post("/auth/logout", {}); } catch { /* silent */ }
    localStorage.removeItem("auth_token");
    navigate("/admin");
  };

  const createGallery = async () => {
    if (!galleryForm.title.trim()) { toast.error("Gallery title is required."); return; }
    try {
      await api.post("/admin/galleries", {
        title: galleryForm.title.trim(),
        event_type: galleryForm.event_type,
        event_date: galleryForm.event_date || null,
        description: galleryForm.description || "",
        client_email: galleryForm.client_email || null,
      });
      toast.success("Gallery created.");
      setGalleryForm({ open: false, title: "", event_type: "Corporate", event_date: "", description: "", client_email: "" });
      await loadGalleries();
    } catch (e) {
      toast.error("Couldn't create gallery.");
    }
  };

  const deleteGallery = async (id, title) => {
    if (!window.confirm(`Delete "${title}" and all its photos? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/galleries/${id}`);
      toast.success("Gallery deleted.");
      await loadGalleries();
    } catch { toast.error("Couldn't delete gallery."); }
  };

  const saveEvent = async () => {
    if (!eventForm.title.trim()) { toast.error("Event title is required."); return; }
    try {
      const payload = {
        title: eventForm.title.trim(),
        event_type: eventForm.event_type,
        event_date: eventForm.event_date,
        location: eventForm.location,
        description: eventForm.description,
      };
      if (eventForm.editing) {
        await api.patch(`/admin/events/${eventForm.editing}`, payload);
        toast.success("Event updated.");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Event created.");
      }
      setEventForm({ open: false, editing: null, title: "", event_type: "Corporate", event_date: "", location: "", description: "" });
      await loadEvents();
    } catch { toast.error("Couldn't save event."); }
  };

  const deleteEvent = async (id, title) => {
    if (!window.confirm(`Delete event "${title}"?`)) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Event deleted.");
      await loadEvents();
    } catch { toast.error("Couldn't delete event."); }
  };

  const setSuggestionStatus = async (id, status) => {
    try {
      await api.post(`/admin/suggestions/${id}/status`, { status });
      toast.success(`Marked as ${status.replace("_", " ")}.`);
      await loadSuggestions();
    } catch { toast.error("Couldn't update."); }
  };

  const convertSuggestionToJob = async (id, message) => {
    if (!window.confirm(`Convert this suggestion to a marketplace job?\n\n"${(message || "").slice(0, 200)}"\n\nARIA agents will auto-bid within 30s.`)) return;
    try {
      const r = await api.post(`/admin/suggestions/${id}/convert-to-job`, {});
      toast.success(`Job created (${r.data.category}). Check the marketplace.`);
      await loadSuggestions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed.");
    }
  };

  if (loading) {
    return (
      <div data-testid="editor-portal-loading" className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
        <p className="text-gold font-body animate-pulse">Loading editor portal...</p>
      </div>
    );
  }

  return (
    <div data-testid="editor-portal" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1">Editor Portal</p>
            <h1 className="font-heading text-3xl text-white">Workspace</h1>
            <p className="text-white/40 font-body text-xs mt-1">
              {me?.email ? <>Signed in as <span className="text-white/60">{me.email}</span></> : null}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <EditorTrustBadge />
            <button data-testid="editor-logout" onClick={handleLogout} className="flex items-center gap-2 text-white/50 hover:text-white text-sm font-body transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-white/5" data-testid="editor-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); if (t.id === "suggestions" && !digest) loadDigest(); }}
                data-testid={`editor-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 font-body text-xs uppercase tracking-wider transition-colors border-b-2 ${active ? "text-gold border-gold" : "text-white/40 border-transparent hover:text-white/70"}`}
              >
                <Icon size={14} /> {t.label}
                {t.id === "suggestions" && (suggestions.counts.new || 0) > 0 && (
                  <span className="ml-1 bg-gold text-luxury-black text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                    {suggestions.counts.new}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* GALLERIES */}
        {tab === "galleries" && (
          <motion.div initial="hidden" animate="visible" data-testid="editor-galleries-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white font-heading text-xl">Galleries</h2>
              <button
                onClick={() => setGalleryForm({ ...galleryForm, open: true })}
                data-testid="editor-create-gallery"
                className="flex items-center gap-2 bg-gold hover:bg-gold/90 text-luxury-black px-4 py-2 text-xs font-body uppercase tracking-wider font-medium transition-colors"
              >
                <Plus size={14} /> New gallery
              </button>
            </div>
            {galleries.length === 0 ? (
              <div className="text-center py-24 border border-white/5">
                <Camera size={40} className="text-white/15 mx-auto mb-4" />
                <p className="text-white/40 font-body text-sm">No galleries yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="editor-gallery-grid">
                {galleries.map((g, i) => (
                  <motion.div
                    key={g.id}
                    variants={fadeUp}
                    custom={i}
                    className="bg-luxury-charcoal border border-white/5 hover:border-gold/40 transition-colors group"
                  >
                    <button
                      onClick={() => navigate(`/admin/gallery/${g.id}`)}
                      data-testid={`editor-gallery-${g.id}`}
                      className="text-left w-full p-5"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2 text-gold/70 text-[10px] font-body uppercase tracking-wider">
                          <ImageIcon size={12} /> {g.event_type || "Gallery"}
                        </div>
                        <ChevronRight size={16} className="text-white/20 group-hover:text-gold transition-colors" />
                      </div>
                      <h3 className="text-white font-heading text-lg leading-snug mb-1">{g.title || "Untitled gallery"}</h3>
                      <p className="text-white/40 text-xs font-body truncate">{g.client_email || "—"}</p>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                        <span className="text-white/30 text-[11px] font-body">{g.media_count ?? 0} photo{(g.media_count ?? 0) === 1 ? "" : "s"}</span>
                        {g.event_date && <span className="text-white/30 text-[11px] font-body">{g.event_date}</span>}
                      </div>
                    </button>
                    <button
                      onClick={() => deleteGallery(g.id, g.title)}
                      data-testid={`editor-delete-gallery-${g.id}`}
                      className="w-full border-t border-white/5 text-red-400/70 hover:text-red-300 hover:bg-red-500/5 py-2 text-[11px] font-body uppercase tracking-wider flex items-center justify-center gap-1.5 transition-colors"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* EVENTS */}
        {tab === "events" && (
          <motion.div initial="hidden" animate="visible" data-testid="editor-events-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white font-heading text-xl">Events</h2>
              <button
                onClick={() => setEventForm({ open: true, editing: null, title: "", event_type: "Corporate", event_date: "", location: "", description: "" })}
                data-testid="editor-create-event"
                className="flex items-center gap-2 bg-gold hover:bg-gold/90 text-luxury-black px-4 py-2 text-xs font-body uppercase tracking-wider font-medium transition-colors"
              >
                <Plus size={14} /> New event
              </button>
            </div>
            {events.length === 0 ? (
              <div className="text-center py-24 border border-white/5">
                <CalendarDays size={40} className="text-white/15 mx-auto mb-4" />
                <p className="text-white/40 font-body text-sm">No events yet — create one to share publicly on the Events page.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="editor-events-grid">
                {events.map((ev, i) => (
                  <motion.div key={ev.id} variants={fadeUp} custom={i} className="bg-luxury-charcoal border border-white/5 p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div className="text-gold/70 text-[10px] font-body uppercase tracking-wider">{ev.event_type || "Event"}</div>
                      <div className="flex gap-1">
                        <button onClick={() => setEventForm({ open: true, editing: ev.id, title: ev.title || "", event_type: ev.event_type || "Corporate", event_date: ev.event_date || "", location: ev.location || "", description: ev.description || "" })} className="p-1 text-white/40 hover:text-white" title="Edit" data-testid={`editor-edit-event-${ev.id}`}>
                          <Edit3 size={13} />
                        </button>
                        <button onClick={() => deleteEvent(ev.id, ev.title)} className="p-1 text-red-400/70 hover:text-red-300" title="Delete" data-testid={`editor-delete-event-${ev.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <h3 className="text-white font-heading text-lg leading-snug mb-1">{ev.title}</h3>
                    <p className="text-white/40 text-xs font-body">{ev.event_date || "Date TBA"}{ev.location ? ` · ${ev.location}` : ""}</p>
                    <p className="text-white/50 text-xs font-body mt-2 line-clamp-2">{ev.description}</p>
                    <div className="flex gap-3 text-[10px] font-body text-white/30 mt-3 pt-3 border-t border-white/5">
                      <span>{ev.media_count ?? 0} media</span>
                      <span>· {ev.attendance ?? 0} check-ins</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* SUGGESTIONS */}
        {tab === "suggestions" && (
          <motion.div initial="hidden" animate="visible" data-testid="editor-suggestions-tab">
            {/* ARIA digest */}
            <div className="bg-luxury-charcoal border border-gold/20 p-6 mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-gold text-[10px] font-body uppercase tracking-[0.25em]">
                  <Sparkles size={13} /> ARIA Weekly Digest
                </div>
                <button onClick={() => loadDigest(true)} className="text-white/50 hover:text-white text-[11px] font-body underline" data-testid="editor-refresh-digest">
                  Refresh
                </button>
              </div>
              {digest ? (
                <>
                  <pre className="text-white/80 font-body text-sm leading-relaxed whitespace-pre-wrap">{digest.summary}</pre>
                  <p className="text-white/30 text-[10px] font-body mt-3">Based on {digest.item_count} suggestion(s) · {digest.cached ? "cached" : "fresh"} · {new Date(digest.generated_at).toLocaleString()}</p>
                </>
              ) : (
                <p className="text-white/40 font-body text-xs animate-pulse">ARIA is reviewing recent suggestions...</p>
              )}
            </div>

            <h3 className="text-white font-heading text-lg mb-4">Recent submissions ({suggestions.items.length})</h3>
            {suggestions.items.length === 0 ? (
              <div className="text-center py-16 border border-white/5">
                <Lightbulb size={36} className="text-white/15 mx-auto mb-4" />
                <p className="text-white/40 font-body text-sm">No suggestions yet. The floating button is live on every public page.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="editor-suggestion-list">
                {suggestions.items.map((s) => (
                  <div key={s.id} className="bg-luxury-charcoal border border-white/5 p-4" data-testid={`editor-suggestion-${s.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-gold/70 text-[10px] font-body uppercase tracking-wider">{s.category || "general"}</span>
                          <span className={`text-[10px] font-body uppercase tracking-wider px-1.5 py-0.5 ${s.status === "new" ? "bg-gold/20 text-gold" : s.status === "actioned" ? "bg-green-500/20 text-green-400" : "text-white/40 bg-white/5"}`}>
                            {s.status}
                          </span>
                          <span className="text-white/30 text-[10px] font-body">{new Date(s.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-white text-sm font-body leading-relaxed">{s.message || s.content || ""}</p>
                        <p className="text-white/40 text-[11px] font-body mt-2">{s.name || "Anonymous"}{s.email ? ` · ${s.email}` : ""}</p>
                      </div>
                    </div>
                    {s.status === "new" && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-white/5 flex-wrap">
                        <button onClick={() => setSuggestionStatus(s.id, "in_review")} data-testid={`suggestion-review-${s.id}`} className="text-xs font-body text-gold hover:text-gold/80 px-2 py-1">Mark in review</button>
                        <button onClick={() => setSuggestionStatus(s.id, "actioned")} data-testid={`suggestion-actioned-${s.id}`} className="text-xs font-body text-green-400 hover:text-green-300 px-2 py-1">Mark actioned</button>
                        <button onClick={() => setSuggestionStatus(s.id, "archived")} data-testid={`suggestion-archive-${s.id}`} className="text-xs font-body text-white/50 hover:text-white px-2 py-1">Archive</button>
                        <button onClick={() => convertSuggestionToJob(s.id, s.message || s.content)} data-testid={`suggestion-convert-${s.id}`} className="text-xs font-body text-purple-300 hover:text-purple-200 px-2 py-1 flex items-center gap-1 ml-auto">
                          <Sparkles size={10} /> Convert → Job
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* GALLERY CREATE MODAL */}
      {galleryForm.open && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setGalleryForm({ ...galleryForm, open: false })}>
          <div className="w-full max-w-md bg-luxury-charcoal border border-gold/20 p-6" onClick={(e) => e.stopPropagation()} data-testid="editor-gallery-form">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-heading text-xl">New gallery</h3>
              <button onClick={() => setGalleryForm({ ...galleryForm, open: false })} className="text-white/40"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="gallery-form-title" value={galleryForm.title} onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })} placeholder="Gallery title *" className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none" />
              <select data-testid="gallery-form-type" value={galleryForm.event_type} onChange={(e) => setGalleryForm({ ...galleryForm, event_type: e.target.value })} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body focus:border-gold/50 outline-none">
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input data-testid="gallery-form-date" type="date" value={galleryForm.event_date} onChange={(e) => setGalleryForm({ ...galleryForm, event_date: e.target.value })} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body focus:border-gold/50 outline-none" />
              <input data-testid="gallery-form-client" type="email" value={galleryForm.client_email} onChange={(e) => setGalleryForm({ ...galleryForm, client_email: e.target.value })} placeholder="Client email (optional)" className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none" />
              <textarea data-testid="gallery-form-desc" value={galleryForm.description} onChange={(e) => setGalleryForm({ ...galleryForm, description: e.target.value })} placeholder="Description (optional)" rows={3} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none resize-none" />
            </div>
            <button onClick={createGallery} data-testid="gallery-form-submit" className="w-full mt-5 bg-gold hover:bg-gold/90 text-luxury-black px-4 py-2.5 font-body text-xs uppercase tracking-wider font-medium transition-colors">Create gallery</button>
          </div>
        </div>
      )}

      {/* EVENT CREATE/EDIT MODAL */}
      {eventForm.open && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEventForm({ ...eventForm, open: false })}>
          <div className="w-full max-w-md bg-luxury-charcoal border border-gold/20 p-6" onClick={(e) => e.stopPropagation()} data-testid="editor-event-form">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-heading text-xl">{eventForm.editing ? "Edit event" : "New event"}</h3>
              <button onClick={() => setEventForm({ ...eventForm, open: false })} className="text-white/40"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="event-form-title" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} placeholder="Event title *" className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none" />
              <select data-testid="event-form-type" value={eventForm.event_type} onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body focus:border-gold/50 outline-none">
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input data-testid="event-form-date" type="date" value={eventForm.event_date} onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body focus:border-gold/50 outline-none" />
              <input data-testid="event-form-location" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder="Location" className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none" />
              <textarea data-testid="event-form-desc" value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} placeholder="Description" rows={3} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none resize-none" />
            </div>
            <button onClick={saveEvent} data-testid="event-form-submit" className="w-full mt-5 bg-gold hover:bg-gold/90 text-luxury-black px-4 py-2.5 font-body text-xs uppercase tracking-wider font-medium transition-colors">{eventForm.editing ? "Save changes" : "Create event"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
